import { useEffect, useState, useRef } from 'react';
import { Alert } from '../../../../types/Alert';
import DeviceAlertTable from './AlertTable';
import DeviceShell from './DeviceShell';
import SummitCharts from './SummitCharts';
import SummitDescription from './SummitDescription';
import SummitTable from './SummitTable';
import AlertService from '../../../../services/alert.service';
import { useParams } from 'react-router-dom';
import BloomTable from './BloomTable';
import { Device, DeviceType } from '../../../../types/Device';
import BloomDescription from './BloomDescription';
import BloomCharts from './BloomCharts';
import SummitCompressorCharts from './SummitCompressorCharts';
import SummitCompressorDescription from './SummitCompressorDescription';
import SummitCompressorTable from './SummitCompressorTable';
import DeviceService from '../../../../services/device.service';
import { DeviceData } from '../../../../types/DeviceData';
import PulseDescription from './PulseDescription';
import PulseDashboard from './PulseDashboard';
import DownloadButton from './DownloadButton';

type DeviceElementMap = {
  [key in DeviceType]: {
    description: JSX.Element;
    dataTable?: JSX.Element;
    charts?: JSX.Element;
    dashboard?: JSX.Element;
    defaultTab?: string;
  };
};

export default function DevicePage() {
  const { siteId, deviceId } = useParams();
  const [deviceData, setDeviceData] = useState<DeviceData[]>([]);
  const [telematicData, setTelematicData] = useState<any[]>([]); //TODO: Cleanup Types
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [highlightedRowId, setHighlightedRowId] = useState<any>(null);
  const [deviceType, setDeviceType] = useState<DeviceType>();
  const latestDataIdRef = useRef<string | null>(null);

  const downloadCsv = async () => {
    try {
      const response = await DeviceService.downloadDeviceDataCsv(deviceId!);

      if (!response.ok) throw new Error(`Failed to download (${response.status})`);

      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const headerFileName = match ? decodeURIComponent(match[1]) : null;

      const blob = await response.blob(); // CSV is fine as a Blob in browsers
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = headerFileName || 'device-data.csv';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const deviceElementMap: DeviceElementMap = {
    [DeviceType.SummitCompressor]: {
      description: <SummitCompressorDescription />,
      dataTable: (
        <div className="relative">
          <DownloadButton onClick={downloadCsv} />
          <SummitCompressorTable alerts={alerts} deviceData={deviceData} highlightedRowId={highlightedRowId} removeHighlightedRowId={setHighlightedRowId} />
        </div>
      ),
      charts: <SummitCompressorCharts deviceData={deviceData} />,
      defaultTab: "data",
    },
    [DeviceType.Summit]: {
      description: <SummitDescription />,
      dataTable: (
        <div className="relative">
          <DownloadButton onClick={downloadCsv} />
          <SummitTable alerts={alerts} deviceData={deviceData} highlightedRowId={highlightedRowId} removeHighlightedRowId={setHighlightedRowId} />
        </div>
      ),
      charts: <SummitCharts deviceData={deviceData} />,
      defaultTab: "data",
    },
    [DeviceType.Bloom]: {
      description: <BloomDescription />,
      dataTable: (
        <div className="relative">
          <DownloadButton onClick={downloadCsv} />
          <BloomTable alerts={alerts} deviceData={deviceData} highlightedRowId={highlightedRowId} removeHighlightedRowId={setHighlightedRowId} />
        </div>
      ),
      charts: <BloomCharts deviceData={deviceData} />,
      defaultTab: "data",
    },
    [DeviceType.Pulse]: {
      description: <PulseDescription />,
      dashboard: <PulseDashboard deviceData={telematicData} />,
      defaultTab: "dashboard",
    },
  };

  const fetchDeviceForDeviceId = async () => {
    if (siteId && deviceId) {
      const deviceResponse = await DeviceService.getDevice(siteId, deviceId);
      const device: Device = await deviceResponse.json();

      setDeviceType(device.type);
      return device;
    }
  }

  const fetchDeviceData = async (deviceType?: DeviceType) => {
    if (deviceId) {
      if (deviceType === DeviceType.Pulse) {
        const telematicResponse = await DeviceService.getTelematicDeviceData(deviceId);
        const telematicData = await telematicResponse.json();
        setTelematicData(telematicData);
      } else {
        const deviceDataResponse = await DeviceService.getDeviceData(deviceId);
        const deviceData = await deviceDataResponse.json();
        setDeviceData(deviceData);
        // Initialize the ref with the latest data ID to prevent duplicate on first poll
        if (deviceData && deviceData.length > 0 && deviceData[0]._id) {
          latestDataIdRef.current = deviceData[0]._id;
        }
      }
    }
  };

  const fetchAlertData = async () => {
    if (deviceId) {
      const alertResponse = await AlertService.searchAlert({ deviceId });
      const data = await alertResponse.json();
      setAlerts(data);
    }
  };

  const handleDeviceDataCreated = (dataId: string) => {
    if (!deviceId) return;

    setHighlightedRowId(dataId);

    DeviceService.getDeviceDataById(deviceId, dataId).then((response) => {
      response.json().then((data: any) => {
        setDeviceData((prev) => [
          { ...data },
          ...prev,
        ]);
      });
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      const device = await fetchDeviceForDeviceId();
      if (!device) return;
      fetchDeviceData(device.type);
    };
    fetchData();
    fetchAlertData();
  }, []);

  // 02/01/2026 - ADD THIS BACK IN WHEN SOCKET ISSUES ARE RESOLVED - REMOVED FOR AHR EXPO
  // useEffect(() => {
  //   if (!socket) return;

  //   socket.on(`device-data.created.${deviceId}`, handleDeviceDataCreated);

  //   return () => {
  //     socket.off(`device-data.created.${deviceId}`);
  //   };
  // }, [socket]);

  // 02/01/2026 - Polling for latest device data every 5 seconds (temporary replacement for websocket)
  useEffect(() => {
    if (!deviceId || deviceType === DeviceType.Pulse) return;

    const pollLatestData = async () => {
      try {
        const response = await DeviceService.getLatestDeviceData(deviceId);
        if (!response.ok) return;
        
        const latestData = await response.json();
        if (!latestData || !latestData._id) return;

        // Only add if this is new data we haven't seen
        if (latestData._id !== latestDataIdRef.current) {
          latestDataIdRef.current = latestData._id;
          
          setDeviceData((prev) => {
            // Check if this data already exists in the array
            if (prev.some((d: any) => d._id === latestData._id)) {
              return prev;
            }
            return [latestData, ...prev];
          });
          // Set highlight after updating data - React batches these together
          setHighlightedRowId(latestData._id);
        }
      } catch (error) {
        console.error('Error polling latest device data:', error);
      }
    };

    const intervalId = setInterval(pollLatestData, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [deviceId, deviceType]);

  return (
    <div className="pt-3 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
      <DeviceShell
        {...(deviceType ? deviceElementMap[deviceType] : {})}
        alertTable={
          <DeviceAlertTable
            alertData={alerts.sort(
              (a: any, b: any) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )}
          />
        }
      />
    </div>
  );
}
